import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Typography } from '@sm/wds-react';
import { Modal } from '@wds/modal';
import { Grid } from '@wds/grid';
import { clientErrorHandler } from '@sm/webassets';
import { generateMetricsAttribute } from '@sm/metrics';
import { IconCheck } from '@sm/wds-icons';
import HubSpotForm from '~pricing/components/HubSpotForm';
import { saveFormData } from '../../../components/HubSpotForm/helpers';
import useHubSpotFormAPI from '~pricing/hooks/useHubSpotAPI';
import NotifyInterestModal from './NotifyInterestModal';
import { buildHubspotPayload, cleanUpFormFields } from '../helpers';
import { LinkIcon } from '../assets';
import COPY from '../assets/EnterpriseFeaturesCopy';
import useStyles from '../useStyles';

export default function AddOnSection({ features, user }) {
  const { addOnContainer } = useStyles();
  const PADDING_X = 9;
  const PADDING_Y = 7;
  const SMALL_COL_BREAKPOINT = 6;
  const LARGE_COL_BREAKPOINT = 4;

  return (
    <Grid className={addOnContainer} my={PADDING_Y} px={PADDING_X}>
      {features.map(feature => {
        return (
          <Grid.Item
            xs={SMALL_COL_BREAKPOINT}
            sm={SMALL_COL_BREAKPOINT}
            md={SMALL_COL_BREAKPOINT}
            lg={LARGE_COL_BREAKPOINT}
            xl={LARGE_COL_BREAKPOINT}
            key={feature.programId}
          >
            <AddOnCard addOn={COPY[feature]} user={user} />{' '}
          </Grid.Item>
        );
      })}
    </Grid>
  );
}

function AddOnCard({ addOn, user }) {
  const [submitted, setSubmitted] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [showHubspotModal, setShowHubspotModal] = useState(false);
  const { addOnCard, addOnCardTitle, bannerButton, checkMark, linkIcon, inlineLink } = useStyles();
  const { data } = useHubSpotFormAPI('1a594719-3256-4c2f-b2ea-06fcb43de45f');
  const formFields = useMemo(() => data?.fields, [data?.fields]);

  const toggleThankYouModal = open => {
    setShowThankYouModal(open);
  };

  const notifyInterest = async programId => {
    const autoFilledFormData = buildHubspotPayload(user);
    const hubspotUrlRoute = `${data?.portalId}/${data?.guid}`;

    try {
      const res = await saveFormData(
        { formFields: cleanUpFormFields(programId, formFields), formData: autoFilledFormData },
        true,
        hubspotUrlRoute
      );
      if (!res?.status?.errors) {
        setSubmitted(true);
        toggleThankYouModal(true);
      }
    } catch (err) {
      clientErrorHandler.logError(err, 'Error notifying sales team of interest.');
    }
  };

  const handleSubmission = () => {
    setShowHubspotModal(false);
    setSubmitted(true);
  };

  if (addOn) {
    const { title, body, image, link, programId, desiredProduct } = addOn;
    const desiredProductInterestSent = localStorage.getItem(`pnp-notify-interest-${desiredProduct}`);

    return (
      <>
        <Grid>
          <div className={addOnCard}>
            <img src={image} alt="add on" />
            {/* None of the variants fit the design */}
            <p className={addOnCardTitle} variant="cardTitle">
              {title}
            </p>
            <Typography variant="body">
              {body}
              <a href={link} className={inlineLink} target="_blank" rel="noreferrer">
                Learn more <img src={LinkIcon} className={linkIcon} alt="link" />
              </a>
            </Typography>
            {submitted || desiredProductInterestSent ? (
              <Button variant="solid" disabled color="alt" className={bannerButton}>
                <IconCheck className={checkMark} size="xs" /> Interest Sent
              </Button>
            ) : (
              <Button
                variant="solid"
                color="alt"
                className={bannerButton}
                onClick={() => {
                  notifyInterest(programId);
                  localStorage.setItem(`pnp-notify-interest-${desiredProduct}`, true);
                }}
                data-sm-metrics={generateMetricsAttribute({
                  data: {
                    desiredProduct,
                    amplitudeEvent: 'pricing clicked enterprise cta',
                  },
                })}
              >
                Notify Interest
              </Button>
            )}
          </div>
        </Grid>
        <NotifyInterestModal showModal={showThankYouModal} toggleModal={toggleThankYouModal} />
        <Modal show={showHubspotModal} size="lg" onClose={() => setShowHubspotModal(false)}>
          <Modal.Body>
            <HubSpotForm customOnSubmitFunction={() => handleSubmission()} />
          </Modal.Body>
        </Modal>
      </>
    );
  }
  return null;
}

AddOnSection.propTypes = {
  features: PropTypes.arrayOf(
    PropTypes.shape({
      body: PropTypes.string,
      image: PropTypes.objectOf(PropTypes.object).isRequired,
      link: PropTypes.string,
      desiredProduct: PropTypes.string,
      programId: PropTypes.string,
    })
  ).isRequired,
  user: PropTypes.objectOf(PropTypes.object).isRequired,
};

AddOnCard.propTypes = {
  addOn: PropTypes.shape({
    title: PropTypes.string,
    body: PropTypes.string,
    desiredProduct: PropTypes.string,
    image: PropTypes.objectOf(PropTypes.object).isRequired,
    link: PropTypes.string,
    programId: PropTypes.string,
  }).isRequired,
  user: PropTypes.objectOf(PropTypes.object).isRequired,
};
